/**
 * Afterpay Integration for Personalised Review Page
 * Refactor: delegate payment to central pay page (/pages/pay-with-afterpay)
 * - Only renders button (with gating) and requests token, then redirects.
 */

(function () {
  'use strict';

  const DEBUG = false;

  // One-time guard
  let __afterpayBound = false;

  // Feature gate
  const enhanceWrapper = document.querySelector('[data-enhance-pricing="true"]');
  if (!enhanceWrapper) return;

  // Mount points
  const afterpayMountDesktop = document.querySelector('[data-enhance-afterpay-target]');
  const afterpayMountMobile = document.querySelector('[data-enhance-afterpay-target-mobile]');
  if (!afterpayMountDesktop && !afterpayMountMobile) {
    if (DEBUG) console.warn('PersonalisedReviewAfterPay: No mount points found');
    return;
  }

  // Responsive
  const mql = window.matchMedia('(max-width: 1200px)');
  let currentAfterpayButton = null;

  function getCurrentMount() {
    return mql.matches ? afterpayMountMobile : afterpayMountDesktop;
  }

  // Overlay
  function showLoadingOverlay() {
    let el = document.getElementById('afterpay-loading-overlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'afterpay-loading-overlay';
      Object.assign(el.style, {
        position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.3)',
        zIndex: '9999', cursor: 'wait', pointerEvents: 'all'
      });
      document.body.appendChild(el);
    }
    el.style.display = 'block';
  }
  function hideLoadingOverlay() {
    const el = document.getElementById('afterpay-loading-overlay');
    if (el) el.style.display = 'none';
  }

  // Helpers
  function isEditMode() {
    const raw = window.personalised?.utils?.getMode?.();
    return typeof raw === 'string' && raw.toLowerCase() === 'edit';
  }

  function getBundleFrequency() {
    try { return window.personalised?.bundle?.getFrequency?.() ?? null; }
    catch { return null; }
  }

  function isFrequencyEligible() {
    const freq = String(getBundleFrequency() ?? '');
    return ['60', '90'].includes(freq);
  }

  function getCartTotalDollars(cart) {
    return (cart && typeof cart.total_price === 'number') ? cart.total_price / 100 : 0;
  }

  function isWithinThreshold(totalDollars) {
    return totalDollars > 0 && totalDollars <= 2000; // AUD
  }

  function isAustralianTimezone() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return timezone && timezone.startsWith('Australia/');
    } catch {
      return false; // Fallback if timezone detection fails
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  function moveAfterpayButtonToCurrentMount() {
    const targetMount = getCurrentMount();
    if (!targetMount || !currentAfterpayButton) return;

    const existing = targetMount.querySelector('#afterpay-button-review');
    if (existing && existing !== currentAfterpayButton) existing.remove();

    if (currentAfterpayButton.parentElement !== targetMount) {
      targetMount.appendChild(currentAfterpayButton);
      if (DEBUG) console.debug('[Afterpay] moved to', mql.matches ? 'mobile' : 'desktop');
    }
  }

  /**
   * Build/attach button (no SDK here)
   */
  function initializeAfterPayButton(cartData) {
    try {
      // Check if user is in Australian timezone
      if (!isAustralianTimezone()) {
        if (DEBUG) console.warn('Afterpay: Not in Australian timezone');
        return;
      }

      // Check if cart contains non-personalised items
      const hasNonPersonalised = (cartData.items || []).some(item =>
        !item.properties || item.properties['_rc_bundle_parent'] !== 'personalised-pack'
      );
      if (hasNonPersonalised) {
        console.log('[Review Afterpay] Cart contains non-personalised items, hiding button');
        return;
      }

      const totalDollars = getCartTotalDollars(cartData);
      if (!isWithinThreshold(totalDollars)) {
        if (DEBUG) console.warn('Afterpay: amount outside threshold', totalDollars);
        return;
      }

      const activeMount = getCurrentMount();
      if (!activeMount) return;
      activeMount.innerHTML = '';

      // Button UI (copy có thể chuyển sang locale)
      activeMount.innerHTML = `
        <button
          id="afterpay-button-review"
          aria-label="Pay with Afterpay"
          style="
            width: 100%;
            padding: 12px;
            border: none;
            background: #272626;
            color: #b2fce3;
            font-size: 16px;
            border-radius: 9999px;
            cursor: pointer;
          "
        >
          Checkout with Afterpay
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24" height="24" style="vertical-align:middle; margin-left:6px;">
            <path fill="#b2fce3" d="M44,24c0,11.044-8.956,20-20,20S4,35.044,4,24S12.956,4,24,4S44,12.956,44,24z"/>
            <path fill="#000100" d="M23.913,36.113c-0.714,0.427-1.506,0.637-2.291,0.637c-0.771,0-1.534-0.196-2.235-0.602l-7.159-4.133 c-1.422-0.82-2.256-2.291-2.228-3.937c0.028-1.639,0.904-3.089,2.354-3.86l6.907-3.706c1.415-0.757,3.061-0.75,4.399,0.021 c1.268,0.736,2.059,2.073,2.109,3.58c0.028,0.967-0.729,1.779-1.695,1.807c-0.995,0.028-1.779-0.729-1.807-1.695 c-0.007-0.301-0.133-0.525-0.357-0.658c-0.28-0.161-0.651-0.147-0.995,0.035L14.008,27.3c-0.448,0.245-0.504,0.665-0.504,0.834 c-0.007,0.168,0.035,0.595,0.476,0.848l7.159,4.133c0.441,0.252,0.827,0.077,0.974-0.007c0.147-0.091,0.483-0.35,0.469-0.855 l-0.054-1.228c-0.025-0.558,0.581-0.919,1.06-0.632l1.849,1.107c0.207,0.124,0.342,0.348,0.343,0.59 C25.786,33.945,25.236,35.316,23.913,36.113z"/>
            <path fill="#000100" d="M38,20.232c-0.028,1.639-0.911,3.082-2.361,3.86l-6.907,3.706c-0.708,0.378-1.464,0.567-2.214,0.567 c-0.757,0-1.499-0.196-2.165-0.574c-1.261-0.729-2.038-2.059-2.087-3.566c-0.028-0.967,0.729-1.779,1.695-1.807h0.107 c0.955,0,1.644,0.787,1.702,1.741c0.017,0.277,0.128,0.479,0.334,0.599c0.266,0.154,0.63,0.133,0.974-0.049l6.907-3.699 c0.455-0.245,0.504-0.665,0.511-0.834c0-0.175-0.042-0.595-0.483-0.848l-7.152-4.133c-0.441-0.252-0.834-0.077-0.974,0.007 c-0.147,0.091-0.483,0.343-0.469,0.855c0.031,0.441,0.057,0.803,0.078,1.1c0.04,0.57-0.582,0.945-1.067,0.645l-1.848-1.144 c-0.197-0.122-0.328-0.333-0.335-0.564c-0.046-1.551,0.423-3.047,1.842-3.897c1.408-0.841,3.096-0.855,4.525-0.035l7.152,4.133 C37.187,17.115,38.021,18.586,38,20.232z"/>
          </svg>
        </button>
      `;

      // Bind click → request token → redirect to central page
      const btn = document.getElementById('afterpay-button-review');
      if (!btn.hasAttribute('data-click-bound')) {
        btn.setAttribute('data-click-bound', 'true');
        btn.addEventListener('click', async () => {
          // Fresh gates
          const modeOk = !isEditMode();
          const eligible = isFrequencyEligible();
          if (!modeOk || !eligible) {
            if (DEBUG) console.warn('[Review Afterpay] Click blocked — modeOk:', modeOk, 'eligible:', eligible);
            return;
          }

          // If sync is active or pending, wait for it to complete before proceeding
          const syncActiveOrPending = window.personalised?.cartSyncState?.syncInProgress === true ||
                                      window.personalised?.cartSyncState?.syncPending === true;
          if (syncActiveOrPending) {
            if (window.personalised?.modalSpinner) {
              window.personalised.modalSpinner.show('Saving your pack...');
            }
            await new Promise((resolve) => {
              const onDone = () => {
                if (!window.personalised?.cartSyncState?.syncPending) {
                  window.personalised?.eventBus?.off('syncCompleted', onDone);
                  window.personalised?.eventBus?.off('syncFailed', onDone);
                  resolve();
                }
              };
              window.personalised?.eventBus?.on('syncCompleted', onDone);
              window.personalised?.eventBus?.on('syncFailed', onDone);
            });
            if (window.personalised?.modalSpinner) {
              window.personalised.modalSpinner.hide();
            }
          }

          showLoadingOverlay();
          try {
            const cart = await fetchCart();
            const total = (cart.total_price / 100).toFixed(2);
            const thresholdOk = parseFloat(total) > 0 && parseFloat(total) <= 2000;
            
            if (!thresholdOk) {
              alert('Order value exceeded the maximum payment limit with Afterpay.');
              return;
            }

            const AFTERPAY_GATEWAY_API_URL = 'https://lvi88fhcpg.execute-api.ap-southeast-2.amazonaws.com/prod/afterpay-gateway';

            // Create afterpay token
            const tokenRes = await fetch(`${AFTERPAY_GATEWAY_API_URL}/afterpay/checkout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                amount: total,
                currency: 'AUD',
                items: (cart.items || []).map(item => ({
                  name: item.product_title,
                  sku: item.sku || '',
                  quantity: item.quantity,
                  price: {
                    amount: (item.price / 100).toFixed(2),
                    currency: 'AUD',
                  }
                })),
                popupOriginUrl: window.location.href,
                mode: 'express'
              })
            });

            if (!tokenRes.ok) {
              throw new Error('Failed to create Afterpay token');
            }

            const data = await tokenRes.json();

            if (!data?.token) {
              throw new Error('Missing token');
            }

            // Redirect to central page (auto-launch)
            const q = new URLSearchParams({
              token: data.token,
              autostart: '1',
              source: 'review'
            });
            window.location.href = `/pages/pay-with-afterpay?${q.toString()}`;
          } catch (e) {
            if (DEBUG) console.error('Afterpay start error:', e);
            alert('There was an error starting Afterpay.');
          } finally {
            hideLoadingOverlay();
          }
        });
      }

      currentAfterpayButton = document.getElementById('afterpay-button-review');

      // Place in correct mount and unhide
      moveAfterpayButtonToCurrentMount();
      if (activeMount) activeMount.removeAttribute('hidden');

      if (DEBUG) console.log('PersonalisedReviewAfterPay: Button ready');
    } catch (err) {
      if (DEBUG) console.error('PersonalisedReviewAfterPay: init button error', err);
    }
  }

  /**
   * Re-evaluate on frequency/state changes
   */
  async function handleFrequencyChange() {
    try {
      const modeOk = !isEditMode();
      const eligible = isFrequencyEligible();
      const timezoneOk = isAustralianTimezone();

      const desktopMount = document.getElementById('review-afterpay-wrapper');
      const mobileMount = document.getElementById('review-afterpay-wrapper-mobile');
      const activeMount = getCurrentMount();

      const hasButton =
        !!(desktopMount?.querySelector('button, [role="button"]') ||
          mobileMount?.querySelector('button, [role="button"]'));

      if (DEBUG) console.debug('[Review Afterpay] freq change — eligible:', eligible, 'modeOk:', modeOk, 'timezoneOk:', timezoneOk, 'hasButton:', hasButton);

      if (!modeOk || !eligible || !timezoneOk) {
        if (desktopMount) desktopMount.setAttribute('hidden', '');
        if (mobileMount) mobileMount.setAttribute('hidden', '');
        if (hasButton && activeMount) activeMount.innerHTML = '';
        currentAfterpayButton = null;
        return;
      }

      if (!hasButton) {
        // Fetch cart for threshold check then mount button
        try {
          const cart = await fetchCart();
          const total = getCartTotalDollars(cart);
          if (isWithinThreshold(total)) {
            initializeAfterPayButton(cart);
          } else {
            if (DEBUG) console.log('Afterpay: threshold fail', total);
          }
        } catch (e) {
          if (DEBUG) console.error('Cart fetch error (freq change):', e);
        }
      } else {
        moveAfterpayButtonToCurrentMount();
        if (desktopMount) desktopMount.removeAttribute('hidden');
        if (mobileMount) mobileMount.removeAttribute('hidden');
      }
    } catch (e) {
      if (DEBUG) console.error('PersonalisedReviewAfterPay: handleFrequencyChange error', e);
    }
  }

  /**
   * Main entry when bundle sync completes
   */
  async function handleSyncCompleted() {
    try {
      if (isEditMode()) return;
      if (!isFrequencyEligible()) return;
      if (!isAustralianTimezone()) return;

      // Fetch cart & mount button if within threshold
      const cart = await fetchCart();
      const total = getCartTotalDollars(cart);
      if (isWithinThreshold(total)) {
        initializeAfterPayButton(cart);
      } else if (DEBUG) {
        console.log('Afterpay: amount outside threshold', total);
      }
    } catch (e) {
      if (DEBUG) console.error('handleSyncCompleted error:', e);
    }
  }

  function init() {
    if (__afterpayBound) return;
    if (!window.personalised || !window.personalised.eventBus) {
      if (DEBUG) console.warn('PersonalisedReviewAfterPay: deps not found');
      return;
    }

    window.personalised.eventBus.on('syncCompleted', handleSyncCompleted);

    if (!window.__prAfterpayFreqBound) {
      window.__prAfterpayFreqBound = true;
      window.personalised?.eventBus?.on?.('renderComplete', handleFrequencyChange);
      window.personalised?.eventBus?.on?.('bundleStateLoaded', handleFrequencyChange);

      mql.addEventListener('change', () => {
        if (DEBUG) console.debug('[Afterpay] Breakpoint changed, mobile:', mql.matches);
        moveAfterpayButtonToCurrentMount();
      });
    }

    __afterpayBound = true;
    if (DEBUG) console.log('PersonalisedReviewAfterPay: Initialized');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
